<template>
  <div class="message-page">
    <div class="page-header">
      <div>
        <div class="page-title">违规消息中心</div>
        <div class="page-subtitle">
          未读消息：{{ summary.unreadCount || 0 }}
          <span v-if="summary.highRisk" class="risk-flag">存在高风险待处理消息</span>
        </div>
      </div>
      <div class="header-actions">
        <el-button @click="fetchSummary">刷新汇总</el-button>
        <el-button v-if="!isManager" type="primary" plain @click="markBatchRead">当前筛选标记已读</el-button>
      </div>
    </div>

    <div class="filter-card">
      <div class="retention-tip">
        扣分消息仅保留最近 7 天，请及时查看并处理，超期后系统会自动清理历史消息。
      </div>
      <div class="filter-row">
        <el-select v-if="isManager" v-model="searchForm.agentId" clearable filterable placeholder="代理" class="filter-item" @change="handleAgentChange">
          <el-option v-for="item in agentOptions" :key="agentOptionValue(item)" :label="agentOptionLabel(item)" :value="agentOptionValue(item)" />
        </el-select>
        <el-select v-model="searchForm.groupId" clearable filterable placeholder="账户分组" class="filter-item" @change="handleGroupChange">
          <el-option v-for="item in advertiserGroupOptions" :key="item.id" :label="item.name" :value="item.id" />
        </el-select>
        <el-select v-model="searchForm.advertiserId" clearable filterable placeholder="广告账户" class="filter-item">
          <el-option
            v-for="item in filteredAdvertiserOptions"
            :key="item.advertiserId"
            :label="item.name + ' / ' + item.advertiserId"
            :value="item.advertiserId"
          />
        </el-select>
        <el-select v-model="searchForm.eventType" clearable placeholder="事件类型" class="filter-item">
          <el-option label="违规积分" value="VIOLATION_SCORE" />
          <el-option label="积分处置" value="DISPOSAL" />
        </el-select>
        <el-select v-model="searchForm.unreadOnly" clearable placeholder="读取状态" class="filter-item">
          <el-option label="仅看未读" :value="1" />
          <el-option label="全部" :value="0" />
        </el-select>
        <el-date-picker
          v-model="searchForm.dateRange"
          type="datetimerange"
          value-format="yyyy-MM-dd HH:mm:ss"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
          class="filter-item date-range"
        />
        <el-input
          v-model.trim="searchForm.keyword"
          placeholder="广告账户 / 标题 / 原因"
          clearable
          class="filter-item keyword-item"
          @keyup.enter.native="handleSearch"
        />
        <el-button type="primary" @click="handleSearch">搜索</el-button>
        <el-button @click="handleReset">重置</el-button>
      </div>
    </div>

    <div class="table-card">
      <el-table :data="tableData" border stripe v-loading="loading" style="width: 100%">
        <el-table-column prop="eventTime" label="时间" min-width="170" />
        <el-table-column label="代理" min-width="160">
          <template slot-scope="{ row }">
            {{ formatAgentDisplay(row.agentId) }}
          </template>
        </el-table-column>
        <el-table-column prop="advertiserId" label="广告账户ID" min-width="140" />
        <el-table-column prop="advertiserName" label="广告账户名称" min-width="180" show-overflow-tooltip />
        <el-table-column prop="eventType" label="类型" width="130">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="row.eventType === 'DISPOSAL' ? 'danger' : 'warning'">
              {{ row.eventType === 'DISPOSAL' ? '积分处置' : '违规积分' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="title" label="标题" min-width="180" show-overflow-tooltip />
        <el-table-column prop="score" label="扣分值" width="100">
          <template slot-scope="{ row }">
            {{ formatScore(row.score) }}
          </template>
        </el-table-column>
        <el-table-column prop="illegalType" label="扣分类型" min-width="160" show-overflow-tooltip />
        <el-table-column prop="rejectReason" label="扣分原因" min-width="260" show-overflow-tooltip />
        <el-table-column prop="summary" label="摘要" min-width="320" show-overflow-tooltip />
        <el-table-column prop="riskLevel" label="风险级别" width="100">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="row.riskLevel === 'HIGH' ? 'danger' : 'warning'">
              {{ row.riskLevel === 'HIGH' ? '高' : (row.riskLevel || '--') }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="unread" label="状态" width="100">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="row.unread === 1 ? 'danger' : 'success'">
              {{ row.unread === 1 ? '未读' : '已读' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="120" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" @click="openMessage(row)">查看</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-wrap">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :current-page="page.pageIndex"
          :page-size="page.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="page.total"
          @current-change="handleCurrentChange"
          @size-change="handleSizeChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import PubSub from "pubsub-js";

const violationScope = require("../../utils/qianchuanViolationScope");

export default {
  name: "ViolationMessageCenter",
  data() {
    return {
      loading: false,
      isManager: false,
      adminInfo: {},
      summary: {
        unreadCount: 0,
        highRisk: false,
        latestMessage: null,
        lastSyncTime: null,
      },
      agentOptions: [],
      advertiserGroupOptions: [],
      advertiserOptions: [],
      tableData: [],
      searchForm: {
        agentId: undefined,
        groupId: undefined,
        advertiserId: undefined,
        eventType: "",
        unreadOnly: undefined,
        keyword: "",
        dateRange: [],
      },
      page: {
        pageIndex: 1,
        pageSize: 10,
        total: 0,
      },
    };
  },
  created() {
    this.adminInfo = this.readAdminInfoFromStorage();
    this.isManager = Number(this.adminInfo.type) === 1;
    this.restoreStoredScope();
    this.loadAgentOptions();
    this.loadAdvertiserGroupOptions();
    this.loadAdvertiserOptions();
    this.fetchSummary();
    this.fetchTableData();
  },
  computed: {
    filteredAdvertiserOptions() {
      if (!this.searchForm.groupId) {
        return this.advertiserOptions || [];
      }
      return (this.advertiserOptions || []).filter((item) => String(item.groupId || "") === String(this.searchForm.groupId));
    },
  },
  methods: {
    agentOptionValue(item) {
      return item.id || item.agentId;
    },
    agentOptionLabel(item) {
      return item.username || item.name || item.agentName || `浠ｇ悊${this.agentOptionValue(item)}`;
    },
    formatAgentDisplay(agentId) {
      const agent = (this.agentOptions || []).find((item) => String(this.agentOptionValue(item)) === String(agentId));
      return agent ? this.agentOptionLabel(agent) : (agentId || "--");
    },
    formatScore(score) {
      if (score === null || score === undefined || score === "") {
        return "--";
      }
      return Number(score).toFixed(2);
    },
    readAdminInfoFromStorage() {
      try {
        return JSON.parse(window.localStorage.getItem("adminInfo") || "{}");
      } catch (error) {
        return {};
      }
    },
    restoreStoredScope() {
      if (!this.isManager) {
        return;
      }
      const storedScope = violationScope.readStoredViolationScope();
      if (storedScope.agentId !== undefined) {
        this.searchForm.agentId = storedScope.agentId;
      }
      if (storedScope.groupId !== undefined) {
        this.searchForm.groupId = storedScope.groupId;
      }
    },
    applySummaryData(data) {
      this.summary = Object.assign({}, this.summary, this.pickMessageSummary(data || {}));
    },
    resetPageData() {
      this.applySummaryData({
        unreadCount: 0,
        highRisk: false,
        latestMessage: null,
        lastSyncTime: null,
      });
      this.tableData = [];
      this.page.total = 0;
    },
    persistScope() {
      if (!this.isManager) {
        return;
      }
      if (!this.searchForm.agentId) {
        violationScope.clearStoredViolationScope();
        return;
      }
      violationScope.writeStoredViolationScope({
        agentId: this.searchForm.agentId,
        groupId: this.searchForm.groupId,
      });
    },
    resolvePageScope(options) {
      return violationScope.resolveViolationScope(
        this.adminInfo,
        {
          agentId: this.searchForm.agentId,
          groupId: this.searchForm.groupId,
        },
        options || {}
      );
    },
    ensurePageScope(showMessage) {
      const scopeState = this.resolvePageScope({
        allowStoredAgentId: true,
      });
      if (!scopeState.ready) {
        this.resetPageData();
        if (showMessage) {
          this.$message.warning("请先选择代理后再加载违规消息");
        }
        return null;
      }
      if (this.searchForm.agentId === undefined && scopeState.scope.agentId !== undefined) {
        this.searchForm.agentId = scopeState.scope.agentId;
      }
      if (this.searchForm.groupId === undefined && scopeState.scope.groupId !== undefined) {
        this.searchForm.groupId = scopeState.scope.groupId;
      }
      this.persistScope();
      return scopeState;
    },
    loadAgentOptions() {
      if (!this.isManager) {
        return;
      }
      this.$axios.post({
        url: "/agent/getAll",
        loading: false,
        success: (res) => {
          if (res.code === 0) {
            this.agentOptions = res.data.data || res.data || [];
          }
        },
      });
    },
    loadAdvertiserOptions() {
      this.$axios.post({
        url: "/oceanEngineAdvertiser/optionsByCondition",
        data: {
          pageIndex: 1,
          pageSize: 30,
        },
        loading: false,
        success: (res) => {
          if (res.code === 0) {
            const payload = res.data || {};
            this.advertiserOptions = payload.content || [];
          }
        },
      });
    },
    loadAdvertiserGroupOptions() {
      this.$axios.post({
        url: "/oceanEngineAdvertiserGroup/getAll",
        loading: false,
        success: (res) => {
          if (res.code === 0) {
            this.advertiserGroupOptions = res.data || [];
          }
        },
      });
    },
    fetchSummary() {
      const scopeState = this.ensurePageScope(false);
      if (!scopeState) {
        return;
      }
      this.$axios.post({
        url: "/qianchuanViolationMessage/summary",
        data: this.buildSummaryRequest(scopeState),
        loading: false,
        success: (res) => {
          if (res.code === 0 && res.data) {
            const nextSummary = this.pickMessageSummary(res.data);
            this.applySummaryData(nextSummary);
            PubSub.publish("refreshViolationMessageSummary", nextSummary);
          }
        },
      });
    },
    buildSummaryRequest(scopeState) {
      return Object.assign({}, scopeState ? scopeState.requestData : {}, {
        advertiserId: this.searchForm.advertiserId || undefined,
        eventType: this.searchForm.eventType || undefined,
        unreadOnly: this.searchForm.unreadOnly === "" ? undefined : this.searchForm.unreadOnly,
        keyword: this.searchForm.keyword || undefined,
        startTime: this.searchForm.dateRange && this.searchForm.dateRange.length ? this.searchForm.dateRange[0] : undefined,
        endTime: this.searchForm.dateRange && this.searchForm.dateRange.length ? this.searchForm.dateRange[1] : undefined,
      });
    },
    pickMessageSummary(data) {
      return {
        unreadCount: data.unreadCount,
        highRisk: data.highRisk,
        latestMessage: data.latestMessage,
        lastSyncTime: data.lastSyncTime,
      };
    },
    fetchTableData() {
      const scopeState = this.ensurePageScope(false);
      if (!scopeState) {
        return;
      }
      this.loading = true;
      this.$axios.post({
        url: "/qianchuanViolationMessage/list",
        data: this.buildRequest(scopeState),
        success: (res) => {
          this.loading = false;
          if (res.code !== 0 || !res.data) {
            this.$message.error(res.msg || "加载消息失败");
            return;
          }
          this.tableData = res.data.content || [];
          this.page.total = res.data.totalElements || 0;
        },
        error: () => {
          this.loading = false;
          this.$message.error("加载消息失败");
        },
      });
    },
    buildRequest(scopeState) {
      return Object.assign({}, scopeState ? scopeState.requestData : {}, {
        advertiserId: this.searchForm.advertiserId || undefined,
        eventType: this.searchForm.eventType || undefined,
        unreadOnly: this.searchForm.unreadOnly === "" ? undefined : this.searchForm.unreadOnly,
        keyword: this.searchForm.keyword || undefined,
        startTime: this.searchForm.dateRange && this.searchForm.dateRange.length ? this.searchForm.dateRange[0] : undefined,
        endTime: this.searchForm.dateRange && this.searchForm.dateRange.length ? this.searchForm.dateRange[1] : undefined,
        pageIndex: this.page.pageIndex,
        pageSize: this.page.pageSize,
      });
    },
    handleSearch() {
      this.page.pageIndex = 1;
      const scopeState = this.ensurePageScope(true);
      if (!scopeState) {
        return;
      }
      this.fetchSummary();
      this.fetchTableData();
    },
    handleAgentChange() {
      this.searchForm.groupId = undefined;
      this.searchForm.advertiserId = undefined;
      this.page.pageIndex = 1;
      if (!this.searchForm.agentId) {
        violationScope.clearStoredViolationScope();
      } else {
        this.persistScope();
      }
      this.fetchSummary();
      this.fetchTableData();
    },
    handleGroupChange() {
      if (this.searchForm.advertiserId && !this.filteredAdvertiserOptions.find((item) => String(item.advertiserId) === String(this.searchForm.advertiserId))) {
        this.searchForm.advertiserId = undefined;
      }
      this.persistScope();
    },
    handleReset() {
      this.searchForm = {
        agentId: this.isManager ? this.searchForm.agentId : undefined,
        groupId: undefined,
        advertiserId: undefined,
        eventType: "",
        unreadOnly: undefined,
        keyword: "",
        dateRange: [],
      };
      this.page.pageIndex = 1;
      this.persistScope();
      this.fetchSummary();
      this.fetchTableData();
    },
    handleCurrentChange(pageIndex) {
      this.page.pageIndex = pageIndex;
      this.fetchTableData();
    },
    handleSizeChange(pageSize) {
      this.page.pageSize = pageSize;
      this.page.pageIndex = 1;
      this.fetchTableData();
    },
    markBatchRead() {
      const scopeState = this.ensurePageScope(false);
      if (!scopeState) {
        return;
      }
      this.$axios.post({
        url: "/qianchuanViolationMessage/readBatch",
        data: Object.assign({}, this.buildSummaryRequest(scopeState)),
        success: (res) => {
          if (res.code !== 0) {
            this.$message.error(res.msg || "标记已读失败");
            return;
          }
          this.$message.success("已标记为已读");
          this.fetchSummary();
          this.fetchTableData();
        },
      });
    },
    openMessage(row) {
      const jump = () => {
        if (row.readableTargetPath) {
          const targetPath = row.readableTargetPath.includes("messageId=")
            ? row.readableTargetPath
            : `${row.readableTargetPath}${row.readableTargetPath.includes("?") ? "&" : "?"}messageId=${encodeURIComponent(row.messageId || "")}`;
          this.$router.push(targetPath);
          return;
        }
        const eventId = this.normalizeNumericQueryValue(row.eventId);
        this.$router.push({
          path: "/qdd/admin/violationRecordDetails.html",
          query: {
            advertiserId: row.advertiserId,
            name: row.advertiserName,
            messageId: row.messageId,
            eventId,
            tab: row.eventType === "DISPOSAL" ? "disposal" : "violation",
          },
        });
      };
      if (this.isManager || row.unread !== 1) {
        jump();
        return;
      }
      this.$axios.post({
        url: "/qianchuanViolationMessage/read",
        data: { messageId: row.messageId },
        loading: false,
        success: () => {
          this.fetchSummary();
          this.fetchTableData();
          jump();
        },
        error: () => {
          jump();
        },
      });
    },
    normalizeNumericQueryValue(value) {
      if (value === null || value === undefined || value === "") {
        return undefined;
      }
      const text = String(value).trim();
      return /^\d+$/.test(text) ? text : undefined;
    },
  },
};
</script>

<style scoped>
.message-page {
  padding: 24px;
  background: #f5f7fb;
  min-height: calc(100vh - 48px);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.page-title {
  font-size: 26px;
  font-weight: 700;
  color: #1f2a44;
}

.page-subtitle {
  margin-top: 8px;
  color: #7b8190;
}

.risk-flag {
  margin-left: 10px;
  color: #f56c6c;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.filter-card,
.table-card {
  background: #fff;
  border-radius: 16px;
  padding: 16px 18px;
  box-shadow: 0 10px 30px rgba(15, 35, 95, 0.06);
}

.table-card {
  margin-top: 16px;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.retention-tip {
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #fff7e6;
  border: 1px solid #ffd591;
  color: #ad6800;
  font-size: 13px;
  line-height: 1.6;
}

.filter-item {
  width: 180px;
}

.date-range {
  width: 340px;
}

.keyword-item {
  width: 280px;
}

.pagination-wrap {
  padding-top: 18px;
  display: flex;
  justify-content: flex-end;
}
</style>
